import { Divider } from '@material-ui/core';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import Paper from '@material-ui/core/Paper';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import fileDownload from 'js-file-download';
import * as yaml from 'js-yaml';
import { ArrowExpandLeft, ArrowExpandRight } from 'mdi-material-ui';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import * as qs from 'qs';
import React, { useEffect, useState, useContext } from 'react';
import AceEditor from "react-ace";
import { LinkProps, RouteComponentProps, withRouter } from 'react-router-dom';
import WindsorApi from '../../api/WindsorApi';
import Gravatar from '../../common/Gravatar';
import LinkRouter from '../../common/LinkRouter';
import LoadingButton from '../../common/LoadingButton';
import LoadingIndicator from '../../common/LoadingIndicator';
import FaroDeckFileTreeHelper from './FaroDeckFileTreeHelper';
import FaroDeckFileTreePanel from './FaroDeckFileTreePanel';
import FaroDeckPackage from './FaroDeckPackage';
import FaroDeckVersionsTable from './FaroDeckVersionsTable';
import MobxContext from '../../mobx/MobxContext';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 1200,
      margin: 'auto'
    },
    header: {
      marginBottom: 24,
    },
    subtitleTypography: {
      fontWeight: 500
    },
    primaryTitleContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 8
    },
    divider: {
      marginBottom: 16,
      marginTop: 8
    },
    createdByPanel: {
      border: `1px solid ${theme.palette.divider}`,
      padding: 16,
      display: 'flex',
      alignItems: 'center',
      marginBottom: 24
    },
    tabs: {
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: darken(theme.palette.background.default, 0.03),
      marginBottom: 24
    },
    notFoundContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      marginTop: 36
    },
    notFoundPaper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: 24,
      marginBottom: 16
    },
    notFoundTitle: {
      marginBottom: 16
    },
    gravatar: {
      width: 24,
      height: 24,
      margin: '0 4px 0 8px'
    },
    tag: {
      margin: '4px 4px 4px 0'
    },
    tagsPanel: {
      marginTop: 8,
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    repositoryButton: {
      marginRight: 8
    },
    showAllTagsLink: {
      marginLeft: 8
    },
    filesTab: {
      display: 'flex'
    },
    treePanelContainer: {
      width: 300, paddingRight: 16
    },
    aceEditorContainer: {
      flex: 1
    },
    filePanelHeader: {
      padding: 16, display: 'flex', alignItems: 'center'
    },
    toggleFileBrowserButton: {
      marginRight: 8
    }
  })
);

const AdapterLink = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => (
  <Link innerRef={ref as any} {...props} />
));

async function getFileDetails(fileNode: any) {
  let fileType = new FaroDeckFileTreeHelper().getFileType(fileNode.id);
  const content = await fileNode.file.buffer();

  let decodedContent = null;
  if (fileType === 'json') {
    decodedContent = JSON.stringify(JSON.parse(content.toString()), null, 2);
  } else if (fileType === 'zip') {
    fileType = 'text';
    decodedContent = `File type "zip" cannot be displayed, please download the deck package to view this file`;
  } else {
    decodedContent = content.toString();
  }

  return {
    id: fileNode.id,
    fileType,
    content: decodedContent
  };
}

const windsorApi = new WindsorApi();

const SHOW_TAGS_LIMIT = 3;
const DEFAULT_TAB = 'files';

function FaroDeckDetailsPage(props: RouteComponentProps<any>) {
  const { match: { params: { deckName } }, location } = props;
  const { tag, version, defaultSelectedFileName, defaultShowFileBrowser = 'true' } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const classes = useStyles({});
  const [tab, setTab] = useState(DEFAULT_TAB);
  const [deck, setDeck] = useState(null);
  const [versions, setVersions] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [rootFileNode, setRootFileNode] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [defaultSelectedFile, setDefaultSelectedFile] = useState(null);
  const [showFileBrowser, setShowFileBrowser] = useState(defaultShowFileBrowser === 'true');
  const [showTagsDialog, setShowTagsDialog] = useState(false);
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const { preferenceStore } = useContext(MobxContext);
  const theme = useTheme();

  useEffect(() => {
    setRootFileNode(null);
    setSelectedFile(null);
    setDefaultSelectedFile(null);
    setDeck(null);
    setTab(DEFAULT_TAB);

    preferenceStore.addRecentlyViewedDeck(deckName);

    windsorApi.getFaroDeck(deckName, version || tag, version ? 'version' : 'tag').then(res => {
      const deckJson = yaml.safeLoad(res);
      setDeck(deckJson);

      windsorApi.getFaroDeckPackage(deckName, deckJson.deckspec.version).then(async data => {
        setRootFileNode(await new FaroDeckPackage(data).build());
      }).catch(error => {
        if (error.status === 404) {
          console.log('Deck package not found')
        }
      });
    }).catch(error => {
      if (error.status === 404) {
        setNotFound(true);
      }
    });

    windsorApi.getFaroDeckVersions(deckName).then(res => {
      setVersions(res.map(item => ({
        deckName: item.DeckName,
        version: item.Version,
        buildDate: item.BuildDate,
        createdBy: item.BuildAttributes && item.BuildAttributes['git.author.name'] ? item.BuildAttributes['git.author.name'] : 'n/a',
        tags: item.Tags || []
      })));
    });
  }, [deckName, version, tag, preferenceStore]);

  useEffect(() => {
    if (rootFileNode && !defaultSelectedFile && !selectedFile) {
      const node = defaultSelectedFileName ? new FaroDeckFileTreeHelper().findFileInTree(rootFileNode, defaultSelectedFileName) : rootFileNode.children[0];
      if (node) {
        getFileDetails(node).then(fileDetails => {
          setSelectedFile(fileDetails);
          setDefaultSelectedFile(fileDetails.id);
        });
      } else {
        console.warn('File not found');
      }
    }
  }, [rootFileNode, defaultSelectedFile, selectedFile, defaultSelectedFileName]);

  const onFileSelected = async (fileNode: any) => {
    if (fileNode.file) {
      setSelectedFile(await getFileDetails(fileNode));
    }
  };

  const onDownloadDeck = () => {
    setDownloadInProgress(true);
    windsorApi.getFaroDeckPackage(deckName, deck.deckspec.version).then(data => {
      setDownloadInProgress(false);
      fileDownload(data, `${deckName}_${deck.deckspec.version}.zip`);
    });
  };

  const onTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setTab(newValue);
  };

  const onToggleFileBrowser = () => {
    setShowFileBrowser(prevValue => !prevValue);
  };

  if (notFound) {
    return <div>
      <div className={classes.notFoundContainer}>
        <Paper className={classes.notFoundPaper}>
          <Typography variant="h6" className={classes.notFoundTitle} >
            Deck Not Found
          </Typography>
          <Typography color="textSecondary" variant="h6">
            Deck with name <strong>{deckName}</strong> and {version ? 'version' : 'tag'} <strong>{version ? version : tag ? tag : 'latest'}</strong> not found
          </Typography>
        </Paper>
      </div>
    </div>;
  } else if (deck && rootFileNode && selectedFile && defaultSelectedFile) {
    return (
      <div className={classes.root}>
        <Breadcrumbs aria-label="breadcrumb">
          <LinkRouter color="textSecondary" to={`/faro/decks`}>
            Decks
          </LinkRouter>
          <Typography color="textPrimary">{deckName}</Typography>
        </Breadcrumbs>
        <Divider className={classes.divider} />
        <div className={classes.header}>
          <div className={classes.primaryTitleContainer}>
            <Typography variant="h6">Deck: {deckName}</Typography>
            <div>
              {deck.deckspec.buildattributes && deck.deckspec.buildattributes['git.repo.url'] && <Button
                component={AdapterLink}
                className={classes.repositoryButton}
                size="small"
                variant="outlined"
                target="_blank"
                rel="noreferrer"
                href={deck.deckspec.buildattributes['git.repo.url']}
              >
                Repository
              </Button>}
              <LoadingButton
                loading={downloadInProgress}
                size="small"
                variant="outlined"
                onClick={onDownloadDeck}>
                Download Deck
              </LoadingButton>
            </div>
          </div>
          <div>
            <Typography className={classes.subtitleTypography} color="textSecondary" display="inline" >
              Version: {deck.deckspec.version}
            </Typography>
          </div>
          <div className={classes.tagsPanel}>
            {deck.tags.slice(0, SHOW_TAGS_LIMIT).map(tag => <Chip key={tag} size="small" className={classes.tag} label={tag} variant="outlined" />)}
            {deck.tags.length > SHOW_TAGS_LIMIT && <Link
              className={classes.showAllTagsLink}
              color="textSecondary"
              onClick={() => setShowTagsDialog(true)}
              variant="body2"
              component="button"
            >
              Show all {deck.tags.length} tags
            </Link>}
          </div>
        </div>
        {deck.deckspec.buildattributes && <div className={classes.createdByPanel}>
          <Typography variant="subtitle1">
            Last updated {moment(deck.deckspec.buildattributes['faro.build.timestamp']).fromNow()} by
          </Typography>
          <Gravatar className={classes.gravatar} email={deck.deckspec.buildattributes['git.author.email'] || deck.deckspec.buildattributes['faro.owners']} />
          <Typography variant="subtitle1">
            {deck.deckspec.buildattributes['git.author.email'] || deck.deckspec.buildattributes['faro.owners']}
          </Typography>
        </div>}
        <Tabs
          className={classes.tabs}
          value={tab}
          onChange={onTabChange}
          aria-label="Parameter Set Details Tabs"
          indicatorColor="primary"
          textColor="primary"
          scrollButtons="auto"
          variant="scrollable"
        >
          <Tab label="Files" value="files" />
          <Tab label="Versions" value="versions" />
        </Tabs>
        {tab === 'files' && <div className={classes.filesTab}>
          {showFileBrowser && <div className={classes.treePanelContainer}>
            <FaroDeckFileTreePanel
              rootNode={rootFileNode}
              defaultSelectedNodeId={defaultSelectedFile}
              onClick={onFileSelected}
            />
          </div>}
          <div className={classes.aceEditorContainer}>
            <Paper>
              <div className={classes.filePanelHeader}>
                <IconButton onClick={onToggleFileBrowser} size="small" className={classes.toggleFileBrowserButton}>
                  {showFileBrowser && <ArrowExpandLeft />}
                  {!showFileBrowser && <ArrowExpandRight />}
                </IconButton>
                <Typography>{selectedFile.id}</Typography>
              </div>
              <Divider />
              <AceEditor
                readOnly
                wrapEnabled
                mode={selectedFile.fileType}
                theme={theme.palette.type === 'light' ? 'chrome' : 'monokai'}
                width="100%"
                minLines={20}
                maxLines={Infinity}
                value={selectedFile.content}
                setOptions={{ useWorker: false, showGutter: true, highlightActiveLine: false }}
                showPrintMargin={false}
                editorProps={{ $blockScrolling: true }}
              />
            </Paper>
          </div>
        </div>}
        {tab === 'versions' && <div>
          {versions && <Paper>
            <FaroDeckVersionsTable versions={versions} />
          </Paper>}
          <LoadingIndicator loading={!versions} />
        </div>}
        {showTagsDialog && <Dialog
          open={true}
          onClose={() => setShowTagsDialog(false)}
          aria-labelledby="tags-dialog-title"
          aria-describedby="tags-dialog-description"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="tags-dialog-title">{deck.tags.length} Tags</DialogTitle>
          <DialogContent>
            {deck.tags.map(tag => <Chip key={tag} size="small" className={classes.tag} label={tag} variant="outlined" />)}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowTagsDialog(false)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>}
      </div>
    );
  } else {
    return <LoadingIndicator loading={true} />;
  }

};

export default withRouter(observer(FaroDeckDetailsPage));
